/**
 * Converts a numeric value and a unit (e.g., Crore, Lakh, Thousand) into a word-based representation.
 *
 * For example:
 *   convertToWordsWithUnit(6, 'CR') → "Six Crore"
 *   convertToWordsWithUnit(2.5, 'L') → "Two Lakh Fifty Thousand"
 *
 * @param {number|string} number - The numeric value to convert. Can be a number or a numeric string.
 * @param {string} unit - The unit of the number. Supported values: "CR" (Crore), "L" (Lakh), "K" (Thousand).
 *
 * @returns {string} - A string representing the number in words with its corresponding unit.
 *                     Returns "Invalid number" or "Invalid unit" if input is not valid.
 */

export function convertToWordsWithUnit(number, unit) {
    if (number === null || number === undefined || isNaN(Number(number))) {
        return 'Invalid number';
    }

    const numberWords = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine',
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen',
    ];

    const tensWords = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    function convertNumberToWords(n) {
        n = Math.round(n);
        if (n === 0) return 'Zero';
        if (n < 20) return numberWords[n];
        if (n < 100) return tensWords[Math.floor(n / 10)] + (n % 10 ? ' ' + numberWords[n % 10] : '');
        if (n < 1000) return numberWords[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertNumberToWords(n % 100) : '');
        if (n < 100000)
            return convertNumberToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convertNumberToWords(n % 1000) : '');
        if (n < 10000000)
            return convertNumberToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convertNumberToWords(n % 100000) : '');
        return convertNumberToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convertNumberToWords(n % 10000000) : '');
    }

    const unitMap = {
        CR: 10000000,
        L: 100000,
        K: 1000,
    };

    if (typeof unit !== 'string' || !unitMap[unit.toUpperCase()]) {
        return 'Invalid unit';
    }

    const multiplier = unitMap[unit.toUpperCase()];
    const value = parseFloat(number) * multiplier;

    return convertNumberToWords(value);
}
